import type { BaseDocument, Ref } from "./data";
import type { Event, EventType } from "./event";
import type { Installation } from "./installation";
import type { Measurement } from "./measurement";
import type {
	LikelihoodResult,
	MeasurementVerification,
	VerificationDecision,
} from "./plausibility";
import type {
	VerdictSuggestionMethod,
	Verification,
	VerificationAnchoredPass,
	VerificationBlindPass,
	VerificationStatus,
} from "./verification";

/** Staff actions on one reanalysis, as `POST .../reanalysis/{id}/{action}`. */
export enum EventReanalysisAction {
	/**
	 * "Check again" on one taking too long: closes it if it is stuck past the
	 * worker's retries, as a new request would, and returns it.
	 */
	Check = "check",
	/** Apply a result the footage did not support anyway. */
	Force = "force",
	/** Undo the most recently applied reanalysis. */
	Revert = "revert",
}

/**
 * Which of the three corrections a reanalysis is. Derived from the staff
 * member's class and the event's current state, never picked, so a request
 * can never contradict itself (see `deriveReanalysisKind` in
 * `@sitescene/shared`).
 *
 * - `missed`: a low-priority event that should have alerted. The staff class
 *   is verified; on support the event rises to at least the alert line.
 * - `falseAlarm`: an alert that was nothing worth mentioning. Stage 1's own
 *   class is verified with the staff class as a named look-alike; on refutal
 *   the event down-ranks as the live verifier would.
 * - `misclassified`: an alert raised for the wrong reason. The staff class is
 *   verified; on support the event is relabeled and keeps its severity.
 */
export enum EventReanalysisKind {
	FalseAlarm = "falseAlarm",
	Misclassified = "misclassified",
	Missed = "missed",
}

/**
 * `confirmed`: the footage supported the correction and it was applied.
 * `notConfirmed`: it did not, and nothing changed unless staff forced it.
 */
export enum EventReanalysisOutcome {
	Confirmed = "confirmed",
	NotConfirmed = "notConfirmed",
}

/** Which app the request came from. Both write the same collection. */
export enum EventReanalysisSource {
	Dashboard = "dashboard",
	ShadowRealm = "shadowRealm",
}

/**
 * - `queued`: inserted; `reanalysis.requested` is on its way to the worker.
 * - `running`: the worker has it.
 * - `completed`: scored, and applied when confirmed (or later forced). An
 *   `error` here means the apply stopped part-way (it can still be
 *   reverted), or an Apply anyway did not finish (it can be applied again).
 * - `failed`: the worker could not finish; `error` says why.
 * - `reverted`: it was applied and staff undid it.
 */
export enum EventReanalysisStatus {
	Completed = "completed",
	Failed = "failed",
	Queued = "queued",
	Reverted = "reverted",
	Running = "running",
}

export type CreateEventReanalysisParams = Omit<
	EventReanalysis,
	"createdAt" | "event" | "id" | "installation" | "updatedAt"
> & {
	event: string;
	installation: string;
};

/**
 * A staff member's correction of one event and what the evidence made of it.
 * Tenant database, collection `eventreanalyses`.
 *
 * Deliberately not a `Verification` row: a reanalysis is anchored on a staff
 * member's answer and drawn only from events someone thought were wrong, so it
 * must never reach calibration, replay, or the verification list. The ground
 * truth it carries reaches calibration the proper way, as a `Verdict` on the
 * pipeline verification when one exists.
 */
export interface EventReanalysis extends BaseDocument {
	/** What the event said once the correction landed. */
	after?: EventReanalysisSummarySnapshot;
	appliedAt?: Date;
	/**
	 * What the event said when the request was made, retaken when the
	 * correction is applied so it is exactly what the apply replaced; revert
	 * writes it back.
	 */
	before: EventReanalysisSummarySnapshot;
	/**
	 * The class the event currently names, as the verifier reads Stage 1:
	 * the pipeline verification's class when one exists, else the alias table.
	 * Absent when neither could place it.
	 */
	currentClassId?: string;
	error?: string;
	event: Ref<Event>;
	/** Set when staff applied a `notConfirmed` result anyway. */
	forced?: EventReanalysisActor;
	installation: Ref<Installation>;
	kind: EventReanalysisKind;
	/** One entry per measurement the evidence passes ran on. */
	measurements: EventReanalysisMeasurement[];
	/**
	 * True while `queued` or `running`, unset after. A partial unique index on
	 * `event` where this is true is what keeps two reanalyses of one event from
	 * running at once, even when two staff press Submit together.
	 */
	open?: boolean;
	outcome?: EventReanalysisOutcome;
	/** `PLAUSIBILITY_PROMPT_VERSION` the passes ran under. */
	promptVersion?: string;
	requestedAt: Date;
	/** Kinde `sub` of the staff member, from whichever app they used. */
	requestedBy: string;
	requestedByName?: string;
	reverted?: EventReanalysisActor;
	source: EventReanalysisSource;
	status: EventReanalysisStatus;
	/** What the class suggester proposed from `whatHappened`, kept only while staff kept it. */
	suggestedClassId?: string;
	suggestionMethod?: VerdictSuggestionMethod;
	/** The class staff say the clip really showed; `none` for nothing alertable. */
	targetClassId: string;
	/**
	 * Staff's account of what the clip shows. Feeds the class suggestion and
	 * the finding the event's new summary is written from, so it may reach the
	 * customer — reworded, never quoted.
	 */
	whatHappened: string;
	/** Staff's note on what the event got wrong. Internal: becomes the verdict note. */
	whatWasWrong?: string;
}

export interface EventReanalysisActor {
	at: Date;
	/** Kinde `sub`. */
	by: string;
	byName?: string;
}

/** Everything a reanalysis panel needs, in one read. */
export interface EventReanalysisContext {
	/** See `EventReanalysis.currentClassId`. */
	currentClassId?: string;
	currentClassName?: string;
	/** Event severity right now; the missed / false-alarm split turns on it. */
	eventSeverity: number;
	/** False when no measurement has a clip: there is nothing to reanalyze. */
	hasClip: boolean;
}

/** One measurement's run, written pass by pass as the worker goes. */
export interface EventReanalysisMeasurement {
	/** Null when the call ran and returned nothing usable. */
	anchored?: null | VerificationAnchoredPass;
	/** Severity the apply step wrote. */
	appliedSeverity?: number;
	blind?: null | VerificationBlindPass;
	/** Set when `blind` was taken from the pipeline verification, not re-run. */
	blindReusedFrom?: Ref<Verification>;
	/** The class the anchored pass was told was suggested. */
	candidateClassId: string;
	/** Look-alikes it was asked to check, including any staff class added. */
	confuserIds: string[];
	decision?: VerificationDecision;
	escalationThreshold?: number;
	/** What the summary will be written from, when this measurement changes. */
	finding?: string;
	/**
	 * Severity the correction implies regardless of the evidence, written by
	 * the scoring step for "Apply anyway": at least the alert line for a
	 * missed or relabeled event, the low-priority ceiling for a false alarm.
	 */
	forcedSeverity?: number;
	likelihood?: LikelihoodResult;
	logPosteriorOdds?: number;
	logPriorOdds?: number;
	measurement: Ref<Measurement>;
	/** Measurement severity when the run started. */
	originalSeverity: number;
	/** The pipeline verification of the same measurement, when one exists. */
	pipelineVerification?: Ref<Verification>;
	posterior?: number;
	/** Taken once at apply, before the measurement changes, for revert. */
	previousSeverity?: number;
	/**
	 * Taken once at apply, before the measurement changes, for revert; null
	 * when the measurement had none.
	 */
	previousVerification?: MeasurementVerification | null;
	/** Severity-policy output for this measurement under this kind. */
	proposedSeverity?: number;
	/** Why this measurement could not be scored. */
	skipReason?: EventReanalysisSkipReason;
	/** True when this measurement's evidence landed the correction. */
	supported?: boolean;
}

export type EventReanalysisSkipReason =
	| "anchoredUnparseable"
	| "clipMissing"
	| "clipTooLarge"
	| "noSpec"
	| "unknownClass";

export interface EventReanalysisSummarySnapshot {
	description?: string;
	severity: number;
	title?: string;
	types: EventType[];
}

/** `GET .../reanalysis`: the panel's context and the event's history, newest first. */
export interface EventReanalysisView {
	context: EventReanalysisContext;
	reanalyses: EventReanalysis[];
}

/** A class the correction panel's dropdown offers. */
export interface ReanalysisClassOption {
	alertable: boolean;
	classId: string;
	description?: string;
	displayName: string;
}

/** Detail of `reanalysis.requested`: which row, and whose database it is in. */
export interface ReanalysisRequestedDetail {
	reanalysisId: string;
	/** Tenant id, so the worker can open the right database. */
	tenantId: string;
}

/** `POST .../reanalysis/suggest`: the class read from staff's words. */
export interface ReanalysisSuggestion {
	alertable: boolean;
	/** A taxonomy class id, or `none` for nothing alertable. */
	classId: string;
	displayName?: string;
	matchedAlias?: string;
	method: VerdictSuggestionMethod;
}

/** Body of `POST .../reanalysis`. */
export interface RequestReanalysisInput {
	suggestedClassId?: string;
	suggestionMethod?: VerdictSuggestionMethod;
	targetClassId: string;
	whatHappened: string;
	whatWasWrong?: string;
}

export interface ShadowEventMeasurement {
	description?: string;
	hasClip: boolean;
	id: string;
	severity: number;
	startTimestamp: string;
	type: string;
	/** The pipeline verification of this measurement, when one ran. */
	verification?: {
		decision?: VerificationDecision;
		id: string;
		status: VerificationStatus;
	};
}

/** `GET /shadow/events/{eventId}`: the event as the Shadow Realm lookup shows it. */
export interface ShadowEventSummary {
	description?: string;
	endTimestamp: string;
	id: string;
	measurements: ShadowEventMeasurement[];
	severity: number;
	startTimestamp: string;
	status: string;
	title?: string;
	types: EventType[];
}
